/**
 * Customer notification drafts of a confirmed product verdict AFFECTED (DEV-100):
 * one Benachrichtigung per instance running the version, one draft per customer.
 * Rendered inside the notification workspace (NotificationsPage); assessing
 * happens in the triage.
 *
 * `POST …/products/{version}/{cve}/email` is MUTATING despite its name: it
 * (re-)runs the Benachrichtigung fan-out (one card per ProduktInstanz) and only then
 * drafts one e-mail per Kunde from the saved Security Advisory. That is why it
 * runs when the panel opens rather than on render, and why it is guarded
 * against React's StrictMode double-mount:
 * a second POST would be harmless but would log a second fan-out run.
 *
 * Nothing is really sent in this MVP — the operator opens the draft in their mail
 * client (`mailto:`) and then stamps the Benachrichtigung cards as sent, which is
 * the record the CRA audit relies on.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import Icon from '../../components/Icon';
import Spinner, { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  ApiError,
  errorMessage,
  fetchEmailDrafts,
  markBenachrichtigungVersendet,
  saveNotificationDraft,
} from '../../lib/api';
import { fmtValue } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { EmailDraft, EmailDraftsResponse, EmailDraftsResult } from '../../lib/types';

import MarkdownEditor from '../../components/markdown/MarkdownEditor';
import { benachrichtigungIds, offeneBenachrichtigungIds } from './eventHelpers';
import { markdownToHtml, markdownToPlainText } from './mailText';

export interface NotificationDraftsProps {
  /** The confirmed AFFECTED product assessment: version × CVE. */
  versionId: number;
  cve: string;
  /** Saved advisory revision — a new revision reloads the drafts. */
  advisoryRevision: number;
  /** `notification.send` — false means read-only: drafts visible, no stamping. */
  canSend: boolean;
  /** Called after every successful stamp so the parent can refetch the row. */
  onStamped: () => void;
  /** Leads to the advisory editor when no advisory is saved. */
  onWriteAdvisory: () => void;
  /** Unsaved edits in any draft (for the close guard). */
  onDirtyChange?: (dirty: boolean) => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; result: EmailDraftsResult }
  | { kind: 'error'; message: string; status: number | null };

/**
 * Clipboard with both formats: mail clients paste the HTML (headings, lists,
 * tables), plain-text targets the text. Falls back to plain text where the
 * browser cannot write HTML.
 */
async function copyFormatted(html: string | null, plain: string): Promise<'html' | 'plain'> {
  if (html !== null && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    await navigator.clipboard.write([new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([plain], { type: 'text/plain' }),
    })]);
    return 'html';
  }
  await navigator.clipboard.writeText(plain);
  return 'plain';
}

export function NotificationDrafts({
  versionId,
  cve,
  advisoryRevision,
  canSend,
  onStamped,
  onWriteAdvisory,
  onDirtyChange,
}: NotificationDraftsProps): ReactNode {
  const toast = useToast();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  /** Benachrichtigung ids stamped in this dialog — server-confirmed, not optimistic. */
  const [stamped, setStamped] = useState<ReadonlySet<number>>(new Set());
  const [markingKunde, setMarkingKunde] = useState<number | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  /** Edited subject/text per customer; absent = the server draft unchanged. */
  const [edits, setEdits] = useState<Readonly<Record<number, { betreff: string; inhalt: string }>>>({});
  /** Last saved text per customer in this session (the baseline of `edits`). */
  const [savedTexts, setSavedTexts] = useState<Readonly<Record<number, { betreff: string; inhalt: string; at: string }>>>({});
  /** Bumped to reload the drafts, e.g. after a saved draft was discarded. */
  const [reload, setReload] = useState(0);
  const [savingKunde, setSavingKunde] = useState<number | null>(null);
  /**
   * The in-flight (or completed) fan-out request, keyed by event.
   *
   * StrictMode mounts effects twice in development, and this POST re-runs the
   * Benachrichtigung fan-out — so it must fire exactly once. Guarding with a
   * plain "already started" flag is NOT enough: the first cleanup would cancel
   * the only subscription while the second pass skips the request altogether,
   * leaving the dialog on its spinner forever. Caching the promise instead lets
   * the second pass re-subscribe to the same request.
   */
  const request = useRef<{ key: string; promise: Promise<EmailDraftsResponse> } | null>(null);

  useEffect(() => {
    const key = `${versionId}:${cve}:${advisoryRevision}:${reload}`;
    let cancelled = false;
    const cached = request.current;
    const promise =
      cached !== null && cached.key === key ? cached.promise : fetchEmailDrafts(versionId, cve);
    request.current = { key, promise };
    promise.then(
      (response) => {
        if (!cancelled) setState({ kind: 'ready', result: response });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: errorMessage(err),
          status: err instanceof ApiError ? err.status : null,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [versionId, cve, advisoryRevision, reload]);

  const baseOf = useCallback(
    (draft: EmailDraft) => savedTexts[draft.kunde.id] ?? { betreff: draft.betreff, inhalt: draft.inhalt },
    [savedTexts],
  );
  const textOf = useCallback(
    (draft: EmailDraft) => edits[draft.kunde.id] ?? baseOf(draft),
    [edits, baseOf],
  );
  const isDirty = useCallback((draft: EmailDraft): boolean => {
    const edited = edits[draft.kunde.id];
    const base = baseOf(draft);
    return edited !== undefined && (edited.betreff !== base.betreff || edited.inhalt !== base.inhalt);
  }, [edits, baseOf]);

  const drafts = state.kind === 'ready' ? state.result.drafts : [];
  const anyDirty = drafts.some(isDirty);
  useEffect(() => onDirtyChange?.(anyDirty), [anyDirty, onDirtyChange]);

  const saveDraft = useCallback(async (draft: EmailDraft, discard = false): Promise<void> => {
    const ids = offeneBenachrichtigungIds(draft, stamped);
    if (ids.length === 0) return;
    const text = textOf(draft);
    setSavingKunde(draft.kunde.id);
    setMarkError(null);
    try {
      // Saved as Markdown: it stays editable; plain text is made when sending.
      await saveNotificationDraft({ benachrichtigung_ids: ids, betreff: text.betreff, inhalt: text.inhalt, discard });
      if (discard) {
        setSavedTexts((current) => { const next = { ...current }; delete next[draft.kunde.id]; return next; });
        setEdits((current) => { const next = { ...current }; delete next[draft.kunde.id]; return next; });
        // The draft comes from the advisory again.
        setReload((r) => r + 1);
        toast.success('Gespeicherter Entwurf verworfen — der Text kommt wieder aus dem Advisory.');
      } else {
        setSavedTexts((current) => ({ ...current, [draft.kunde.id]: { ...text, at: new Date().toISOString() } }));
        toast.success('Entwurf gespeichert.');
      }
    } catch (err) {
      setMarkError(errorMessage(err));
    } finally {
      setSavingKunde(null);
    }
  }, [stamped, textOf, toast]);

  const markDraft = useCallback(
    async (draft: EmailDraft): Promise<void> => {
      const ids = offeneBenachrichtigungIds(draft, stamped);
      if (ids.length === 0) return;
      // The customer receives plain text; that is also what is stored as sent.
      const edited = textOf(draft);
      const text = {
        betreff: edited.betreff,
        inhalt: draft.source === 'sent' ? edited.inhalt : markdownToPlainText(edited.inhalt),
      };
      setMarkingKunde(draft.kunde.id);
      setMarkError(null);
      const confirmed: number[] = [];
      try {
        // Sequential on purpose: the first failure must stop the round and be
        // reported with the server's German detail instead of half-applying.
        for (const id of ids) {
          const response = await markBenachrichtigungVersendet(id, {
            empfaenger: draft.kunde.email ?? '',
            betreff: text.betreff,
            inhalt: text.inhalt,
          });
          confirmed.push(response.result.id);
        }
      } catch (err) {
        setMarkError(errorMessage(err));
      } finally {
        if (confirmed.length > 0) {
          setStamped((current) => {
            const next = new Set(current);
            for (const id of confirmed) next.add(id);
            return next;
          });
          onStamped();
        }
        setMarkingKunde(null);
      }
    },
    [stamped, onStamped, textOf],
  );

  const busy = markingKunde !== null || savingKunde !== null;

  return (
    <div className="notification-drafts">
      {state.kind === 'loading' ? (
        <PageSpinner text="Benachrichtigungen werden erzeugt…" />
      ) : state.kind === 'error' ? (
        <Banner
          kind="error"
          sub={
            state.status === 409
              ? 'Bestätigen Sie die Produktversion zuerst in der Triage als „Betroffen“.'
              : state.status === 404
                ? 'Die Produktversion existiert nicht mehr, laden Sie die Liste neu.'
                : 'Es wurde keine Benachrichtigung erzeugt.'
          }
        >
          {state.message}
        </Banner>
      ) : (
        <>
          {markError === null ? null : (
            <Banner kind="error" sub="Nicht alle Benachrichtigungen wurden als versendet markiert.">
              {markError}
            </Banner>
          )}

          {state.result.drafts.length === 0 ? (
            <Banner kind="warn">
              Keine betroffenen Kunden: Nach bekannter Konfiguration läuft keine Produktinstanz mit
              dieser Komponentenversion.
            </Banner>
          ) : (
            <Banner kind="info">
              {state.result.drafts.length}{' '}
              {state.result.drafts.length === 1 ? 'Kunde' : 'Kunden'} betroffen, ein Entwurf pro
              Kunde. Passen Sie den Text bei Bedarf an, versenden Sie ihn im eigenen Mailprogramm und
              markieren Sie ihn danach hier als versendet — gespeichert wird der versendete Text.
            </Banner>
          )}

          {state.result.drafts.some((draft) => draft.source === 'missing') ? (
            <Banner
              kind="warn"
              sub={<button type="button" className="ghost small" onClick={onWriteAdvisory}>Security Advisory verfassen</button>}
            >
              Kein Security Advisory gespeichert: Ohne Advisory gibt es keinen Entwurf. Übernehmen Sie die
              Kurz-Vorlage oder die CRA-Vorlage, passen Sie sie an und speichern Sie sie.
            </Banner>
          ) : null}

          {state.result.drafts.map((draft) => {
            const allIds = benachrichtigungIds(draft);
            const openIds = offeneBenachrichtigungIds(draft, stamped);
            const marking = markingKunde === draft.kunde.id;
            const text = textOf(draft);
            const missing = draft.source === 'missing';
            const editable = canSend && openIds.length > 0 && !missing;
            // Drafts are Markdown; mail clients get readable plain text.
            const plain = draft.source === 'sent' ? text.inhalt : markdownToPlainText(text.inhalt);
            const mailto = draft.kunde.email
              ? `mailto:${encodeURIComponent(draft.kunde.email)}?subject=${encodeURIComponent(text.betreff)}&body=${encodeURIComponent(plain)}`
              : null;
            const setText = (next: { betreff: string; inhalt: string }): void =>
              setEdits((current) => ({ ...current, [draft.kunde.id]: next }));
            return (
              <article className="mail" key={draft.kunde.id}>
                <div className="to">
                  <strong>{fmtValue(draft.kunde.name)}</strong> · An:{' '}
                  {draft.kunde.email ? (
                    draft.kunde.email
                  ) : (
                    <span className="muted">(keine E-Mail hinterlegt)</span>
                  )}{' '}
                  · {draft.instanzen.length} Instanz(en)
                </div>
                <ChipList>
                  <Chip tone={missing ? 'warn' : 'info'}>
                    {savedTexts[draft.kunde.id] || draft.source === 'saved'
                      ? `Gespeicherter Entwurf${draft.saved_by && !savedTexts[draft.kunde.id] ? ` · ${draft.saved_by}` : ''}`
                      : draft.source === 'advisory' ? 'Aus dem Security Advisory'
                        : draft.source === 'sent' ? 'Versendeter Text' : 'Kein Entwurf — Security Advisory fehlt'}
                  </Chip>
                  {isDirty(draft) ? <Chip tone="warn">Ungespeicherte Änderungen</Chip> : null}
                  {draft.placeholders > 0 && editable ? (
                    <Chip tone="warn" title="Platzhalter {{…}} vor dem Versand ersetzen">
                      {draft.placeholders} offene Platzhalter
                    </Chip>
                  ) : null}
                </ChipList>
                {editable ? (
                  <>
                    <label className="visually-hidden" htmlFor={`mail-subject-${draft.kunde.id}`}>Betreff</label>
                    <input
                      type="text"
                      id={`mail-subject-${draft.kunde.id}`}
                      className="mail-subject-input"
                      value={text.betreff}
                      disabled={busy}
                      onChange={(e) => setText({ ...text, betreff: e.target.value })}
                    />
                    <span className="visually-hidden" id={`mail-body-label-${draft.kunde.id}`}>Text an {fmtValue(draft.kunde.name)}</span>
                    <div className="mail-body-input">
                      <MarkdownEditor
                        id={`mail-body-${draft.kunde.id}`}
                        labelledBy={`mail-body-label-${draft.kunde.id}`}
                        value={text.inhalt}
                        disabled={busy}
                        rows={9}
                        onChange={(inhalt) => setText({ ...text, inhalt })}
                      />
                    </div>
                    <div className="mail-draft-actions">
                      {editable ? (
                    <>
                      {draft.source === 'saved' || savedTexts[draft.kunde.id] ? (
                        <button type="button" className="ghost small" disabled={busy}
                          title="Gespeicherten Entwurf verwerfen; beim nächsten Öffnen gilt wieder das Advisory"
                          onClick={() => void saveDraft(draft, true)}>
                          Verwerfen
                        </button>
                      ) : null}
                      <button type="button" className="ghost small" disabled={busy || !isDirty(draft)}
                        title="Bearbeiteten Text speichern, um ihn später weiter zu bearbeiten oder zu versenden"
                        onClick={() => void saveDraft(draft)}>
                        {savingKunde === draft.kunde.id ? <Spinner /> : null} Entwurf speichern
                      </button>
                    </>
                  ) : null}
                    </div>
                  </>
                ) : missing ? null : (
                  <>
                    <div className="subj">{text.betreff}</div>
                    <pre>{text.inhalt}</pre>
                  </>
                )}
                <div className="triage-mail-foot">
                  <ChipList>
                    {allIds.length === 0 ? (
                      <Chip tone="warn">Keine Benachrichtigung angelegt</Chip>
                    ) : (
                      <Chip mono>
                        Benachrichtigung {allIds.map((id) => `#${id}`).join(', ')}
                      </Chip>
                    )}
                    {openIds.length === 0 ? (
                      <Chip tone="good">versendet</Chip>
                    ) : (
                      <Chip tone="warn">{openIds.length} offen</Chip>
                    )}
                  </ChipList>
                  <div className="spacer" />
                  {missing ? null : (
                    <>
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() => {
                          void navigator.clipboard.writeText(text.betreff).then(
                            () => toast.success('Betreff kopiert.'),
                            () => toast.error('Kopieren nicht möglich — bitte den Betreff markieren und kopieren.'),
                          );
                        }}
                      >
                        Betreff kopieren
                      </button>
                      <button
                        type="button"
                        className="ghost small"
                        title="Formatiert (HTML) und als Klartext in die Zwischenablage — beim Einfügen ins Mailprogramm bleiben Überschriften, Listen und Tabellen erhalten"
                        onClick={() => {
                          const html = draft.source === 'sent' ? null : markdownToHtml(text.inhalt);
                          void copyFormatted(html, plain).then(
                            (format) => toast.success(format === 'html'
                              ? 'Text formatiert kopiert — im Mailprogramm einfügen.'
                              : 'Text als Klartext kopiert.'),
                            () => toast.error('Kopieren nicht möglich — bitte den Text markieren und kopieren.'),
                          );
                        }}
                      >
                        Text kopieren
                      </button>
                    </>
                  )}
                  {missing ? null : mailto === null ? (
                    <span className="muted"><Icon name="warning" size={14} /> Keine Kontakt-E-Mail hinterlegt</span>
                  ) : (
                    <a href={mailto}>
                      <strong><Icon name="mail" /> Im Mailprogramm öffnen</strong>
                    </a>
                  )}
                  {openIds.length === 0 || missing ? null : canSend ? (
                    <button
                      type="button"
                      className="primary small"
                      onClick={() => void markDraft(draft)}
                      disabled={busy}
                    >
                      {marking ? <Spinner variant="on-accent" /> : null} Als versendet markieren
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="primary small"
                      disabled
                      title={missingPermissionTitle('notification.send')}
                    >
                      Als versendet markieren
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </>
      )}
    </div>
  );
}

export default NotificationDrafts;
